import {
  HTML_ALLOWED_ATTRS,
  HTML_ALLOWED_TAGS,
} from "../../utils/additionalSettings";
import type { FroalaEditorInstance } from "./types";

interface FroalaConfigCallbacks {
  onSaveBefore: (html: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onButtonsRefresh: () => false | void;
  onInitialized: (editor: FroalaEditorInstance) => void;
  onContentChanged: () => void;
  onKeydown: (e: KeyboardEvent) => void;
  onPasteBefore: (e: ClipboardEvent) => false | void;
  onPasteAfterCleanup: (html: string) => string;
  onCharCounterUpdate: () => void;
}

export const buildFroalaConfig = (
  saveInterval: number,
  callbacks: FroalaConfigCallbacks,
) => ({
  key: import.meta.env.VITE_FROALA_KEY,
  // Variable pills are contenteditable=false atoms (shared/utils/variable-pill).
  // By default Froala's format.apply/applyStyle step over non-editable nodes,
  // so "select all → italic" left the pill upright. With this on, Froala wraps
  // the whole run — `<em>Hello <pill/> world</em>` — so the token inherits the
  // style and serialises as `<em>Hello {{x}} world</em>`. Verified against the
  // exact CDN build (froala-v2.js, 5.0.0) in isolation; the same wrapping now
  // also encloses other inline atoms (icons, emoji) inside a formatted run,
  // which is the semantically right result for them too.
  allowStylingOnNonEditable: true,
  pastePlain: false,
  pasteDeniedAttrs: ["class", "id", "dir", "font", "o:p"],
  pasteDeniedTags: ["img", "svg", "script"],
  // Text elements never hold images (pasteDeniedTags already denies <img> in
  // pasted HTML). Disable Froala's separate clipboard-image-blob paste too, or
  // copying text from PowerPoint — which puts BOTH a picture and the text on
  // the clipboard — inserts the image alongside the text. Bug 11445195655.
  imagePaste: false,
  htmlRemoveTags: ["script", "style"],
  keepFormatOnDelete: true,
  toolbarContainer: ".toolbarContainer",
  placeholderText: "Start typing here...",
  charCounterCount: true,
  attribution: false,
  shortcutsEnabled: [
    "show",
    "bold",
    "italic",
    "underline",
    "strikeThrough",
    "indent",
    "outdent",
  ],
  pluginsEnabled: [
    "align",
    "colors",
    "entities",
    "fontFamily",
    "fontSize",
    "lineBreaker",
    "lineHeight",
    "link",
    "lists",
    "paragraphFormat",
    "save",
    "table",
    "charCounter",
  ],
  htmlAllowedTags: HTML_ALLOWED_TAGS,
  htmlAllowedAttrs: HTML_ALLOWED_ATTRS,
  saveInterval,
  quickInsertEnabled: false,
  shortcuts: {
    "ctrl+z": false,
    "ctrl+y": false,
    "cmd+z": false,
    "cmd+y": false,
  },
  events: {
    "save.before": callbacks.onSaveBefore,
    focus: callbacks.onFocus,
    blur: callbacks.onBlur,
    "buttons.refresh": function (this: FroalaEditorInstance) {
      return callbacks.onButtonsRefresh();
    },
    initialized: function (this: FroalaEditorInstance) {
      callbacks.onInitialized(this);
    },
    contentChanged: callbacks.onContentChanged,
    keydown: function (this: FroalaEditorInstance, e: KeyboardEvent) {
      callbacks.onKeydown.call(this, e);
    },
    "paste.before": function (this: FroalaEditorInstance, e: any) {
      return callbacks.onPasteBefore.call(this, e);
    },
    "paste.afterCleanup": function (this: FroalaEditorInstance, html: string) {
      return callbacks.onPasteAfterCleanup(html);
    },
    "paste.after": function (this: FroalaEditorInstance) {
      this.save.save();
    },
    "charCounter.update": callbacks.onCharCounterUpdate,
  },
});
